import { useQueryClient } from "@tanstack/react-query";

import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { updateBooleanInActivationMetadata } from "app/pages/activation/helpers/home/updateBooleanInActivationMetadata";
import { TABS_ARRAY } from "app/pages/activation/modules/Settings/config";
import { SettingsTabs } from "app/pages/activation/modules/Settings/types";
import {
    ACTIVATION_PROJECT_ROUTE,
    ACTIVATION_SETTINGS_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActivationMetadataItem } from "app/pages/activation/types/api";
import { useSubscribeToAllAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAllAlertsMutation";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";

type UseItemSchema = {
    activation: ActivationMetadataItem;
};

export const useItem = ({ activation }: UseItemSchema) => {
    const queryClient = useQueryClient();
    const track = activationTracking.homePageWithData;

    const { isSubscribed, country, id, primaryDomain } = activation;
    const { mutate: subscribe } = useSubscribeToAllAlertsMutation({ subscribeToBenchmark: true });

    const countryObject = CountryService.getCountryById(country);

    const onViewClick = () => {
        track.trackViewProject();

        void proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: id,
            country,
            webSource: ActivationComponentAssets.defaultSource,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
        });
    };

    const onEmailClick = () => {
        subscribe(
            { activationId: id },
            {
                onSuccess: () => {
                    updateBooleanInActivationMetadata({
                        queryClient,
                        itemId: id,
                        objectKey: "isSubscribed",
                        value: true,
                    });
                },
            },
        );
    };

    const onRouteSubscriptions = () => {
        void proNavigator.go(ACTIVATION_SETTINGS_ROUTE, {
            domain: primaryDomain,
            key: id,
            country,
            webSource: ActivationComponentAssets.defaultSource,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            tab: TABS_ARRAY.indexOf(SettingsTabs.Subscriptions),
        });
    };

    const onSettingsClick = () => {
        void proNavigator.go(ACTIVATION_SETTINGS_ROUTE, {
            domain: primaryDomain,
            key: id,
            country,
            webSource: ActivationComponentAssets.defaultSource,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            tab: 0,
        });
    };

    return {
        isSubscribed,
        countryObject,
        onViewClick,
        onEmailClick,
        onRouteSubscriptions,
        onSettingsClick,
    };
};
